// STUB: vector client surface used by `VectorToolCard` (WP-4) until the
// real `@geosoft/viewer-sdk` ships an `client.vector` namespace per
// `geosoft-platform-strategy/docs/appendix/sdk-drafts/viewer-sdk.md`.
//
// This module exists so the ToolCard does not import any third-party
// library directly and the export pathway can be unit-tested through a
// public seam (`__setBlobAdapter`).

export type VectorExportFormat = 'geojson' | 'csv' | 'flatgeobuf';

/** Properties bag for one vector feature (matches GeoJSON.Feature.properties). */
export type VectorFeatureProperties = Readonly<Record<string, unknown>>;

/** Minimal feature shape the export entry-point understands. */
export interface VectorExportFeature {
  readonly id?: string | number;
  readonly properties: VectorFeatureProperties;
  readonly geometry?: unknown;
}

export interface VectorExportSelectionInput {
  readonly layerId: string;
  readonly features: ReadonlyArray<VectorExportFeature>;
  readonly format?: VectorExportFormat;
  readonly filename?: string;
}

export interface VectorExportSelectionResult {
  readonly mimeType: string;
  readonly filename: string;
  readonly byteLength: number;
  readonly format: VectorExportFormat;
}

interface BlobAdapter {
  /** Build a blob from a payload (string or array of strings). */
  toBlob(payload: string, mimeType: string): Blob | null;
  /** Click a hidden anchor to start the download. */
  triggerDownload(blobUrl: string, filename: string): void;
}

const defaultBlobAdapter: BlobAdapter = {
  toBlob(payload, mimeType) {
    if (typeof Blob === 'undefined') return null;
    return new Blob([payload], { type: mimeType });
  },
  triggerDownload(blobUrl, filename) {
    if (typeof document === 'undefined') return;
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = filename;
    anchor.rel = 'noopener';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  },
};

let blobAdapter: BlobAdapter = defaultBlobAdapter;

/** Test seam used by `__tests__/exportSelection.test.ts`. */
export function __setBlobAdapter(next: BlobAdapter | null): void {
  blobAdapter = next ?? defaultBlobAdapter;
}

function mimeFor(format: VectorExportFormat): string {
  switch (format) {
    case 'csv':
      return 'text/csv';
    case 'flatgeobuf':
      return 'application/octet-stream';
    case 'geojson':
    default:
      return 'application/geo+json';
  }
}

function buildGeoJson(features: ReadonlyArray<VectorExportFeature>): string {
  return JSON.stringify({
    type: 'FeatureCollection',
    features: features.map((feature) => ({
      type: 'Feature',
      id: feature.id,
      properties: feature.properties,
      geometry: feature.geometry ?? null,
    })),
  });
}

function buildCsv(features: ReadonlyArray<VectorExportFeature>): string {
  if (features.length === 0) return '';
  const keys = Array.from(
    new Set(
      features.flatMap((feature) => Object.keys(feature.properties ?? {})),
    ),
  );
  const header = ['id', ...keys].join(',');
  const rows = features.map((feature) => {
    const cells = [
      JSON.stringify(feature.id ?? ''),
      ...keys.map((key) => JSON.stringify(feature.properties?.[key] ?? '')),
    ];
    return cells.join(',');
  });
  return [header, ...rows].join('\n');
}

/**
 * Mock export entry-point invoked by `<VectorToolCard>` when the user
 * presses the export-selection action. The real SDK will publish a
 * lossless writer pipeline; until then this writes a client-side blob
 * for GeoJSON / CSV and a stub placeholder for FlatGeobuf so the UX is
 * testable end-to-end.
 */
export function exportSelection(
  input: VectorExportSelectionInput,
): VectorExportSelectionResult {
  const format = input.format ?? 'geojson';
  const filename =
    input.filename ?? `${input.layerId}-selection.${extensionFor(format)}`;
  const payload =
    format === 'csv'
      ? buildCsv(input.features)
      : format === 'flatgeobuf'
        ? `# flatgeobuf placeholder for ${input.layerId} (${input.features.length} features)`
        : buildGeoJson(input.features);

  const mimeType = mimeFor(format);
  const blob = blobAdapter.toBlob(payload, mimeType);
  if (blob && typeof URL !== 'undefined' && URL.createObjectURL) {
    const url = URL.createObjectURL(blob);
    try {
      blobAdapter.triggerDownload(url, filename);
    } finally {
      if (typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(url);
      }
    }
  }
  return {
    mimeType,
    filename,
    byteLength: payload.length,
    format,
  };
}

function extensionFor(format: VectorExportFormat): string {
  switch (format) {
    case 'csv':
      return 'csv';
    case 'flatgeobuf':
      return 'fgb';
    case 'geojson':
    default:
      return 'geojson';
  }
}

/**
 * Namespace exposed as the public seam consumed by `<VectorToolCard>`.
 * Mirrors `client.vector.*` on the planned real SDK.
 */
export const vectorClient = {
  exportSelection,
};

export type VectorClient = typeof vectorClient;
